/**
 * Example Lit Web Component
 *
 * This component demonstrates how Lit web components can be used alongside
 * AngularJS and Angular in the hybrid application.
 *
 * Key benefits of Lit:
 * 1. Framework-agnostic - works in AngularJS, Angular, or vanilla HTML
 * 2. Small footprint (~5KB)
 * 3. Fast rendering with lit-html
 * 4. Native web components - no framework lock-in
 *
 * Usage:
 * - In AngularJS: <example-card title="Hello" variant="info"></example-card>
 * - In Angular: <example-card [title]="'Hello'" [variant]="'info'"></example-card>
 * - In HTML: <example-card title="Hello" variant="info"></example-card>
 */
import { LitElement, html, css } from 'lit';
import { customElement, property } from 'lit/decorators.js';

export type CardVariant = 'default' | 'info' | 'success' | 'warning' | 'danger';

@customElement('example-card')
export class ExampleCard extends LitElement {
  static override styles = css`
    :host {
      display: block;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    }

    .card {
      border-radius: 8px;
      padding: 16px;
      margin: 8px 0;
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
      transition: box-shadow 0.2s ease;
    }

    .card:hover {
      box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
    }

    .card--default {
      background: #ffffff;
      border: 1px solid #e0e0e0;
    }

    .card--info {
      background: #e3f2fd;
      border: 1px solid #2196f3;
    }

    .card--success {
      background: #e8f5e9;
      border: 1px solid #4caf50;
    }

    .card--warning {
      background: #fff3e0;
      border: 1px solid #ff9800;
    }

    .card--danger {
      background: #ffebee;
      border: 1px solid #f44336;
    }

    .card__header {
      display: flex;
      align-items: center;
      margin-bottom: 12px;
    }

    .card__icon {
      width: 24px;
      height: 24px;
      margin-right: 8px;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .card__title {
      font-size: 16px;
      font-weight: 600;
      margin: 0;
    }

    .card__badge {
      display: inline-block;
      padding: 2px 8px;
      border-radius: 12px;
      font-size: 11px;
      font-weight: 500;
      margin-left: auto;
      background: rgba(0, 0, 0, 0.1);
    }

    .card__content {
      font-size: 14px;
      line-height: 1.5;
      color: #333;
    }

    .card__footer {
      margin-top: 12px;
      padding-top: 12px;
      border-top: 1px solid rgba(0, 0, 0, 0.1);
      font-size: 12px;
      color: #666;
    }
  `;

  @property({ type: String })
  cardTitle = 'Lit Component';

  @property({ type: String })
  variant: CardVariant = 'default';

  @property({ type: Boolean })
  showBadge = true;

  private getIcon() {
    const icons: Record<CardVariant, string> = {
      default: '📦',
      info: 'ℹ️',
      success: '✅',
      warning: '⚠️',
      danger: '❌',
    };
    return icons[this.variant];
  }

  override render() {
    return html`
      <div class="card card--${this.variant}">
        <div class="card__header">
          <span class="card__icon">${this.getIcon()}</span>
          <h4 class="card__title">${this.cardTitle}</h4>
          ${this.showBadge ? html`<span class="card__badge">Lit 3.x</span>` : ''}
        </div>
        <div class="card__content">
          <slot>Default content - pass content as children</slot>
        </div>
        <div class="card__footer">
          Rendered by Lit Web Components
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'example-card': ExampleCard;
  }
}

